import React, { useContext, useState } from 'react'
import { API_HOST, keycloak } from 'lib'
import { authenticatedFetch, authenticatedPost } from 'lib/authenticatedGet'
import { FileType, JobForInstallation, ResponseObject } from 'domain/DTO'
import { AssortmentStatusCell } from 'components/AssortmentStatusCell'
import { logError } from '../../../lib/logError'
import SearchIcon from '../../../icons/SearchIcon'
import { Dialog } from '../../../components/Dialog'
import { InstallationViewDialog } from './InstallationViewDialog'
import { hasRestoreOverridesPermission, hasWriteInstallationPermission } from '../../../lib/Permission'
import { CommonLink } from './CommonLink'
import { InstallationViewDTO } from '../../../domain/InstallationViewDTO'
import { SelectedCountryContext } from '../../../domain/SelectedCountryContext'
import { Toast, ToastState } from '../../../components/Toast'
import { OverrideHistory } from './OverrideHistory'

type RestoreOverridesTriggerOutcome = 'ACTION_ENSURED' | 'NO_PENDING_REMOVAL' | 'NO_PRESERVED_MODIFICATIONS'

type EnsuredAction = {
  manualActionId: string
  matchedPreviousInstallationId: string
  uninstalledAt: string
}

type RestoreOverridesTriggerResult = {
  outcome: RestoreOverridesTriggerOutcome
  candidateCount: number
  ensuredAction: EnsuredAction | null
}

type InstallationHeaderProps = {
  installationView: InstallationViewDTO
  refresh: () => void
  history: JobForInstallation[]
  commonIsLocked: boolean
  onOverridesRecovered: () => void
}

export const InstallationHeader = ({
  installationView,
  refresh,
  history,
  commonIsLocked,
  onOverridesRecovered,
}: InstallationHeaderProps) => {
  const [cooldown, setCooldown] = useState(false)
  const [showOverrideDialog, setShowOverrideDialog] = useState(false)
  const [toast, setToast] = useState<ToastState>(null)
  const { installation } = installationView
  const { selectedCountry } = useContext(SelectedCountryContext)

  const enabledAssortmentStatuses = [
    { type: FileType.SHAPE, status: installationView.shapeStatus },
    { type: FileType.BARCODE, status: installationView.barcodeStatus },
    { type: FileType.CRATE, status: installationView.crateStatus },
    { type: FileType.LOCAL_CRATE, status: installationView.localCrateStatus },
    { type: FileType.ARTIKELDATEN, status: installationView.artikeldatenStatus },
  ].filter((item) => item.status.enabledForCommonAssortment)

  const triggerUploadAndRefresh = (installationId: string) => {
    const warningText =
      'The RVM you are about to update is linked to a locked CommonConfig. This CommonConfig may be locked because it currently has an inconsistency. Are you sure you want to update anyway?'
    if (commonIsLocked && !window.confirm(warningText)) {
      setShowOverrideDialog(false)
      return
    }
    setCooldown(true)
    return authenticatedFetch(`${API_HOST}/v1/admin/trigger-generation/${installationId}`, { method: 'POST' })
      .catch((error) => logError(error))
      .finally(() => {
        setTimeout(() => {
          setCooldown(false)
          refresh()
        }, 3000)
      })
  }

  const triggerOverrideOfInstallations = async (installationIdSource: string, installationIdTargets: string[]) => {
    const warningText =
      'You are about to override all assortment for the selected machines. Any overrides will be replaced by overrides on this machine. Are you sure?'
    if (commonIsLocked && !window.confirm(warningText)) return

    setCooldown(true)
    const form = new FormData()
    authenticatedPost(
      `${API_HOST}/v1/installation/${installationIdSource}/overrides?installationTargets=${installationIdTargets}`,
      { body: form },
      600000,
    )
      .catch((err) => alert('Failed to override installations: ' + err.message))
      .finally(() => {
        setCooldown(false)
        refresh()
      })
  }

  const triggerRestoreOverridesCheck = async (installationId: string) => {
    setCooldown(true)
    try {
      const response = await authenticatedPost<ResponseObject<RestoreOverridesTriggerResult>>(
        `${API_HOST}/v1/admin/restore-overrides/${installationId}`,
        { body: new FormData() },
        120000,
      )
      const result = response.data
      if (result.outcome === 'ACTION_ENSURED') {
        const matched = result.ensuredAction
          ? ` (matched previous installation ${result.ensuredAction.matchedPreviousInstallationId})`
          : ''
        setToast({ variant: 'success', message: `Restore-overrides inbox entry ensured${matched}` })
      } else if (result.outcome === 'NO_PENDING_REMOVAL') {
        setToast({ variant: 'info', message: 'No pending removal found for this installation.' })
      } else {
        setToast({
          variant: 'info',
          message: `No preserved modifications found among ${result.candidateCount} candidate(s).`,
        })
      }
    } catch (error) {
      logError(error)
      setToast({
        variant: 'error',
        message: `Failed to check for restorable overrides: ${(error as Error).message}`,
      })
    } finally {
      setCooldown(false)
      refresh()
    }
  }

  const exportAssortmentFiles = async (installationId: string) => {
    try {
      const url = `${API_HOST}/bff/installations/${installationId}/export-files?country=${selectedCountry}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
        },
      })

      if (!response.ok) {
        const message = `Failed response with status ${response.status}`
        throw new Error(message)
      }

      const contentDisposition = response.headers.get('content-disposition') || ''
      const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
      const filename = match ? decodeURIComponent(match[1]) : `installation-${installationId}-export.zip`

      const blob = await response.blob()

      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error: Error) {
      logError(error)
      alert('Failed to export files: ' + error.message)
    } finally {
      refresh()
    }
  }

  const hasAnyOngoingJobs = history.some((h) => h.concluded == null)
  const disabled = cooldown || hasAnyOngoingJobs

  return (
    <div>
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <div>
        <h2 className="mx-sm" style={{ fontSize: '2rem', marginBottom: '1rem' }}>
          {installation.installationReference.serialNumber}
        </h2>
      </div>
      <div style={{ display: 'flex', gap: '1rem' }}>
        {hasWriteInstallationPermission() && (
          <button
            className="btn btn-primary-dark py-sm"
            disabled={disabled}
            title={disabled ? 'Unable to Upload Latest when ongoing update' : ''}
            onClick={() => triggerUploadAndRefresh(installation.installationReference.installationId)}
          >
            Upload Latest
          </button>
        )}
        {hasWriteInstallationPermission() && (
          <button
            className="btn btn-primary-dark py-sm"
            disabled={disabled}
            title={
              disabled
                ? 'Unable to override installation when ongoing update'
                : 'Copy all override configurations from this installation to others'
            }
            onClick={() => setShowOverrideDialog(true)}
          >
            Duplicate all overrides to ...
          </button>
        )}
        {hasRestoreOverridesPermission() && (
          <button
            className="btn btn-primary-dark py-sm"
            disabled={disabled}
            title={
              disabled
                ? 'Unable to check for restorable overrides when ongoing update'
                : 'Re-run the replacement-detection check for this installation'
            }
            onClick={() => triggerRestoreOverridesCheck(installation.installationReference.installationId)}
          >
            Check for restorable overrides
          </button>
        )}
        <OverrideHistory
          installationId={installation.installationReference.installationId}
          currentCommonId={installation.commonId}
          currentCommonName={installation.commonName}
          onRecovered={onOverridesRecovered}
        />
        <button
          className="btn btn-primary-dark"
          title="Export assortment files before sending an update"
          onClick={() => exportAssortmentFiles(installation.installationReference.installationId)}
        >
          Export files
        </button>
      </div>
      {showOverrideDialog && (
        <Dialog onClose={() => setShowOverrideDialog(false)}>
          <InstallationViewDialog
            edit={{
              commonId: installation.commonId || '',
              installationIdSource: installation.installationReference.installationId,
            }}
            onComplete={async (installationIdTargets: string[]) => {
              const filteredTargets = installationIdTargets.filter(
                (target) => target !== installation.installationReference.installationId,
              )
              if (filteredTargets.length > 0) {
                await triggerOverrideOfInstallations(installation.installationReference.installationId, filteredTargets)
              }
              setShowOverrideDialog(false)
            }}
          />
        </Dialog>
      )}
      <div className="mt-md flex flex-col md:flex-row gap-8 mb-2">
        <div className="flex-2">
          <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0">
            <div className="font-bold text-right">Machine family:</div>
            <div>{installation.installationReference.machineFamily}</div>

            {installation.installationReference.materialGroup && (
              <>
                <div className="font-bold text-right">Material Group:</div>
                <div>{installation.installationReference.materialGroup}</div>
              </>
            )}
            <div className="font-bold text-right">Chain / Sub Chain:</div>
            <div>{installation.installationReference.chainName ?? installation.installationReference.subChainName}</div>

            <div className="font-bold text-right">Location:</div>
            <div>{`${installation.installationReference.locationName} [${installation.installationReference.country}${
              installation.installationReference.region ? ', ' + installation.installationReference.region : ''
            }]`}</div>

            {installation.commonId && (
              <>
                <div className="font-bold text-right">Common:</div>
                <div>
                  <div>
                    <CommonLink to={`/common-assortment/${installation.commonId}`}>
                      {'📑 ' + installation.commonName}
                    </CommonLink>
                  </div>
                  <div>
                    <CommonLink to={`/installations?commonId=${installation.commonId}`} className="inline-flex mt-xs">
                      <SearchIcon color="none" size="18" />
                      <span className="ml-xs">{'Installations (' + installation.commonName + ')'}</span>
                    </CommonLink>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {installation.commonId && (
          <div className="flex-2">
            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2">
              <div className="font-bold text-right">Latest Status:</div>
              <div>
                {installation.machineConfig === null && (
                  <div style={{ marginBottom: '10px', color: 'red' }}>
                    Missing machine configuration, unable to create correct update script. (Usually resolves with a OPC
                    Sync message)
                  </div>
                )}
                {enabledAssortmentStatuses.length > 0 && (
                  <AssortmentStatusCell assortmentStatuses={enabledAssortmentStatuses} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
